import { useState, useEffect } from 'react';
import type { AxiosError } from 'axios';
import { fetchEmployeeDetail, fetchEmployeeFiles, type EmployeeDetail, type EmployeeFileItem } from '../api/employees';

export function useEmployeeDetail(employeeId: number | undefined) {
  const [employee, setEmployee] = useState<EmployeeDetail | null>(null);
  const [files, setFiles] = useState<EmployeeFileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!employeeId) {
      setEmployee(null);
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [emp, fileList] = await Promise.all([
          fetchEmployeeDetail(employeeId),
          fetchEmployeeFiles(employeeId).catch(() => [])
        ]);
        setEmployee(emp);
        setFiles(fileList);
      } catch (err) {
        console.error('Erreur lors du chargement de l\'employé:', err);
        const ax = err as AxiosError;
        const status = ax.response?.status;
        if (status === 404) setError('Employé non trouvé');
        else if (status === 401) setError('Session expirée. Veuillez vous reconnecter.');
        else setError('Impossible de charger les détails de l\'employé');
        setEmployee(null);
        setFiles([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [employeeId]);

  const refresh = async () => {
    if (!employeeId) return;
    try {
      const [emp, fileList] = await Promise.all([
        fetchEmployeeDetail(employeeId),
        fetchEmployeeFiles(employeeId).catch(() => [])
      ]);
      setEmployee(emp);
      setFiles(fileList);
    } catch (err) {
      console.error('Erreur lors du refresh employé:', err);
    }
  };

  return { employee, files, loading, error, refresh, setFiles };
}
